import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import _ from 'underscore';
import {
  Card,
  CardHeader,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui/copilot-i18n';
import { IQuestion } from '@bingads-webui/search-utils';
import { useAdsCopilotIsSendDisabled } from '@bingads-webui/ads-copilot-ui';
import {
  ActionIcon,
  InsightIcon,
  SupportIcon,
  TroubleshootingIcon,
} from './icons/index';

export type CopilotWelcomeTileProps = {
  category: IQuestion['category'];
  className?: string;
  onClick?: (event: React.SyntheticEvent<HTMLDivElement, Event>, text: string, category: string) => void;
  onRenderWelcomeTile?: (text: string, category: string) => void,
  text: string;
};

const useStyles = makeStyles({
  root: {},
  rootDisabled: {
    pointerEvents: 'none',
  },
  wrapper: {},
  wrapperDisabled: {
    cursor: 'not-allowed',
  },
  header: {
    maxWidth: '100%',
    gridAutoRows: '1fr min-content',
  },
  headerWithNewIcon: {
    alignItems: 'start',
  },
  headerText: {
    overflowX: 'hidden',
    wordBreak: 'break-word',
  },
  headerImage: {
    maxWidth: 'fit-content',
    alignSelf: 'center',
  },
});

export const CopilotWelcomeTile: React.FC<CopilotWelcomeTileProps> = React.memo((props) => {
  const {
    category,
    className,
    onClick,
    onRenderWelcomeTile,
    text,
  } = props;

  const i18n = useContext(I18nContext);
  const classes = useStyles();
  const [isFirstRender, setIsFirstRender] = useState(true);
  const isSendDisabled = useAdsCopilotIsSendDisabled();

  useEffect(() => {
    if (!_.isEmpty(text) && !_.isEmpty(category) && onRenderWelcomeTile && isFirstRender) {
      onRenderWelcomeTile(text, category);
      setIsFirstRender(false);
    }
  }, [text, category, onRenderWelcomeTile, isFirstRender]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isSendDisabled) {
      return;
    }
    onClick?.(event, text, category);
  }, [onClick, text, category, isSendDisabled]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isSendDisabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      onClick?.(event, text, category);
    }
  }, [onClick, text, category, isSendDisabled]);

  const image = useMemo(() => {
    let Component: React.FC<{ title: string | undefined }> | undefined;
    let title: string | undefined;

    if (category === 'action') {
      title = i18n?.getString(_TL_('Action category'));
      Component = ActionIcon;
    }
    if (category === 'insight') {
      title = i18n?.getString(_TL_('Insight category'));
      Component = InsightIcon;
    }
    if (category === 'support' || category === 'troubleshooting') {
      title = i18n?.getString(_TL_('Support category'));
      Component = SupportIcon;
    }
    if (category === 'troubleshooting') {
      title = i18n?.getString(_TL_('Troubleshooting category'));
      Component = TroubleshootingIcon;
    }

    if (Component) {
      return <Component title={title} />;
    }
    return undefined;
  }, [category, i18n]);

  return (
    <div className={isSendDisabled ? classes.wrapperDisabled : classes.wrapper}>
      <Card
        className={mergeClasses('copilot-welcome-tile', isSendDisabled ? classes.rootDisabled : classes.root, className)}
        size="medium"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        focusMode="tab-only"
        aria-disabled={isSendDisabled}
      >
        <CardHeader
          className={mergeClasses(classes.header, classes.headerWithNewIcon, 'copilot-welcome-tile-content')}
          image={{ className: mergeClasses('copilot-welcome-tile-image', classes.headerImage), children: image }}
          header={{ className: mergeClasses('copilot-welcome-tile-text', classes.headerText), children: text }}
        />
      </Card>
    </div>
  );
});
